/* ==========================================================================
   LeeBrant Jewelry & Watch Co. — sliders (Diamond Cellar structure)
   Vanilla JS, no dependencies. Include with:
     <script src="js/slider.js?v=5" defer></script>

   Powers two things:

   1. Crossfade carousels (hero slider AND campaign banner rotation).
      DOM contract:
        <section class="dc-slider" aria-label="...">
          <div class="dc-slider__track">
            <div class="dc-slider__slide" data-label="Engagement Rings">
              <img class="dc-slider__img" src="..." alt="...">   (or <picture>)
              <div class="dc-slider__content ...">eyebrow / title / one button</div>
            </div>
            ...
          </div>
        </section>
      The script injects chevrons + a "1 / 3" counter and a polite live
      region. Without JS the first slide stays visible and no controls
      appear. data-label (or the slide heading text) feeds announcements.

   2. Featured-product strips.
      DOM contract:
        <div class="featured-strip" data-featured-strip data-count="10"
             aria-label="Featured pieces"></div>
      The script fetches data/catalog.json once, picks a category-balanced
      set of priced pieces, and renders a horizontally scrollable strip of
      standard product cards with prev/next chevrons.
   ========================================================================== */

(function () {
  "use strict";

  var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  var AUTO_ADVANCE_MS = 6500;

  var CHEVRON_LEFT =
    '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
    '<path d="M15 4l-8 8 8 8" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
  var CHEVRON_RIGHT =
    '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
    '<path d="M9 4l8 8-8 8" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';

  function makeButton(className, svg, label) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = className;
    btn.innerHTML = svg;
    var hidden = document.createElement("span");
    hidden.className = "visually-hidden";
    hidden.textContent = label;
    btn.appendChild(hidden);
    return btn;
  }

  /* ------------------------------------------------------------------
     1. Crossfade carousel (.dc-slider)
     ------------------------------------------------------------------ */

  function initSlider(root) {
    var slides = Array.prototype.slice.call(
      root.querySelectorAll(".dc-slider__slide")
    );
    if (!slides.length) return;

    root.setAttribute("aria-roledescription", "carousel");
    if (!root.hasAttribute("role")) root.setAttribute("role", "region");

    var current = 0;
    var timer = null;
    var hovered = false;
    var focused = false;

    function slideLabel(i) {
      var slide = slides[i];
      var label = slide.getAttribute("data-label");
      if (!label) {
        var heading = slide.querySelector("h1, h2, h3, .dc-slider__title");
        if (heading) label = heading.textContent.replace(/\s+/g, " ").trim();
      }
      return label || "";
    }

    slides.forEach(function (slide, i) {
      slide.setAttribute("role", "group");
      slide.setAttribute("aria-roledescription", "slide");
      var label = slideLabel(i);
      slide.setAttribute(
        "aria-label",
        (i + 1) + " of " + slides.length + (label ? ": " + label : "")
      );
    });

    // Controls: ‹ 1 / 3 › on a translucent ink pill, plus a polite live
    // region for announcements. Injected only when JS runs.
    var controls = document.createElement("div");
    controls.className = "dc-slider__controls";

    var prevBtn = makeButton("dc-slider__arrow", CHEVRON_LEFT, "Previous slide");
    var nextBtn = makeButton("dc-slider__arrow", CHEVRON_RIGHT, "Next slide");

    var count = document.createElement("span");
    count.className = "dc-slider__count";
    count.setAttribute("aria-hidden", "true");

    controls.appendChild(prevBtn);
    controls.appendChild(count);
    controls.appendChild(nextBtn);
    // A single-slide carousel needs no chrome: no counter, no chevrons
    if (slides.length > 1) root.appendChild(controls);

    var live = document.createElement("p");
    live.className = "visually-hidden";
    live.setAttribute("aria-live", "polite");
    live.setAttribute("aria-atomic", "true");
    root.appendChild(live);

    function announce() {
      var label = slideLabel(current);
      live.textContent =
        "Slide " + (current + 1) + " of " + slides.length +
        (label ? ": " + label : "");
    }

    function render() {
      slides.forEach(function (slide, i) {
        slide.classList.toggle("is-active", i === current);
      });
      count.textContent = (current + 1) + " / " + slides.length;
    }

    function goTo(index, userInitiated) {
      current = (index + slides.length) % slides.length;
      render();
      if (userInitiated) {
        announce();
        restartTimer();
      }
    }

    /* ----- auto-advance: 6.5s, paused on hover/focus/hidden tab ----- */

    function shouldRun() {
      return (
        slides.length > 1 &&
        !reducedMotion.matches &&
        !hovered &&
        !focused &&
        !document.hidden
      );
    }

    function stopTimer() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function startTimer() {
      if (timer || !shouldRun()) return;
      timer = setInterval(function () {
        goTo(current + 1, false);
      }, AUTO_ADVANCE_MS);
    }

    function restartTimer() {
      stopTimer();
      startTimer();
    }

    function syncTimer() {
      if (shouldRun()) {
        startTimer();
      } else {
        stopTimer();
      }
    }

    prevBtn.addEventListener("click", function () { goTo(current - 1, true); });
    nextBtn.addEventListener("click", function () { goTo(current + 1, true); });

    root.addEventListener("mouseenter", function () { hovered = true; syncTimer(); });
    root.addEventListener("mouseleave", function () { hovered = false; syncTimer(); });
    root.addEventListener("focusin", function () { focused = true; syncTimer(); });
    root.addEventListener("focusout", function (event) {
      if (!root.contains(event.relatedTarget)) {
        focused = false;
        syncTimer();
      }
    });

    document.addEventListener("visibilitychange", syncTimer);

    // Arrow keys work anywhere inside the carousel region
    root.addEventListener("keydown", function (event) {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goTo(current - 1, true);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goTo(current + 1, true);
      }
    });

    // Reduced motion: static first slide, manual controls still work.
    // The global reduced-motion CSS collapses the crossfade to an
    // instant swap; here we only stop or resume the auto-advance.
    if (typeof reducedMotion.addEventListener === "function") {
      reducedMotion.addEventListener("change", syncTimer);
    }

    root.classList.add("is-ready");
    render();
    startTimer();
  }

  /* ------------------------------------------------------------------
     2. Featured-product strip ([data-featured-strip])
     ------------------------------------------------------------------ */

  var catalogPromise = null;

  function fetchCatalog() {
    if (!catalogPromise) {
      catalogPromise = fetch("data/catalog.json").then(function (res) {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.json();
      });
    }
    return catalogPromise;
  }

  // Category-balanced pick: round-robin across categories, priced items
  // only, in catalog (featured) order. Deterministic on purpose so the
  // strip does not reshuffle on every visit.
  function pickFeatured(items, n) {
    var byCat = {};
    var order = [];
    items.forEach(function (item) {
      if (typeof item.price !== "number") return;
      if (!item.image || !item.name || !item.url) return;
      var cat = item.category || "Other";
      if (!byCat[cat]) {
        byCat[cat] = [];
        order.push(cat);
      }
      byCat[cat].push(item);
    });

    var picks = [];
    var depth = 0;
    while (picks.length < n) {
      var added = false;
      for (var c = 0; c < order.length && picks.length < n; c++) {
        var pool = byCat[order[c]];
        if (depth < pool.length) {
          picks.push(pool[depth]);
          added = true;
        }
      }
      if (!added) break;
      depth++;
    }
    return picks;
  }

  // Same card anatomy as the catalog grid so .product-card styling is
  // reused as-is. The image field may be remote or local; render it as
  // given.
  function buildCard(item) {
    var li = document.createElement("li");
    li.className = "product-card featured-strip__card";

    var a = document.createElement("a");
    a.className = "product-card__link";
    a.href = item.url;
    a.target = "_blank";
    a.rel = "noopener";

    if (item.onSale) {
      var badge = document.createElement("span");
      badge.className = "product-card__badge";
      badge.textContent = "Sale";
      a.appendChild(badge);
    }

    var media = document.createElement("span");
    media.className = "product-card__media";
    var img = document.createElement("img");
    img.src = item.image;
    img.alt = "";
    img.width = 600;
    img.height = 600;
    img.loading = "lazy";
    img.decoding = "async";
    media.appendChild(img);
    a.appendChild(media);

    var body = document.createElement("span");
    body.className = "product-card__body";

    if (item.brand) {
      var brand = document.createElement("span");
      brand.className = "product-card__brand";
      brand.textContent = item.brand;
      body.appendChild(brand);
    }

    var name = document.createElement("span");
    name.className = "product-card__name";
    name.textContent = item.name;
    body.appendChild(name);

    var price = document.createElement("span");
    price.className = "product-card__price";
    price.textContent = item.priceDisplay || "Call for Price";
    body.appendChild(price);

    var hidden = document.createElement("span");
    hidden.className = "visually-hidden";
    hidden.textContent = " (opens the LeeBrant product page in a new tab)";
    body.appendChild(hidden);

    a.appendChild(body);
    li.appendChild(a);
    return li;
  }

  function initFeaturedStrip(root) {
    // SHOPIFY: the home page now renders the strip's cards in Liquid from a
    // real collection, so the viewport and track already exist in the DOM.
    // When that is the case there is nothing to fetch; just hang the chevrons
    // off the markup that is already there. The fetch path below is the
    // static-site fallback and stays untouched.
    var prerendered = root.querySelector(".featured-strip__viewport");
    if (prerendered) {
      wireStrip(root, prerendered);
      return;
    }

    var n = parseInt(root.getAttribute("data-count"), 10);
    if (!n || n < 1) n = 10;

    fetchCatalog()
      .then(function (data) {
        var items = (data && data.items) || [];
        var picks = pickFeatured(items, n);
        if (!picks.length) {
          root.hidden = true;
          return;
        }
        renderStrip(root, picks);
      })
      .catch(function () {
        // Quietly remove the strip; the page reads fine without it.
        root.hidden = true;
      });
  }

  function renderStrip(root, picks) {
    var viewport = document.createElement("div");
    viewport.className = "featured-strip__viewport";
    viewport.tabIndex = 0;
    viewport.setAttribute("role", "group");
    viewport.setAttribute(
      "aria-label",
      (root.getAttribute("aria-label") || "Featured pieces") +
        ", scrollable list"
    );

    var track = document.createElement("ul");
    track.className = "featured-strip__track";
    picks.forEach(function (item) {
      track.appendChild(buildCard(item));
    });
    viewport.appendChild(track);
    root.appendChild(viewport);

    wireStrip(root, viewport);
  }

  // Chevrons and scroll state for a strip viewport, however it was built:
  // rendered above from JSON, or rendered by Liquid on the Shopify home page.
  function wireStrip(root, viewport) {
    var prevBtn = makeButton(
      "featured-strip__nav featured-strip__nav--prev",
      CHEVRON_LEFT,
      "Scroll to previous pieces"
    );
    var nextBtn = makeButton(
      "featured-strip__nav featured-strip__nav--next",
      CHEVRON_RIGHT,
      "Scroll to next pieces"
    );

    root.appendChild(prevBtn);
    root.appendChild(nextBtn);

    function maxScroll() {
      return viewport.scrollWidth - viewport.clientWidth;
    }

    function updateNav() {
      var max = maxScroll();
      root.classList.toggle("featured-strip--static", max <= 1);
      prevBtn.disabled = viewport.scrollLeft <= 1;
      nextBtn.disabled = viewport.scrollLeft >= max - 1;
    }

    function scrollByPage(dir) {
      var amount = Math.max(viewport.clientWidth * 0.85, 240) * dir;
      viewport.scrollBy({
        left: amount,
        behavior: reducedMotion.matches ? "auto" : "smooth"
      });
    }

    prevBtn.addEventListener("click", function () { scrollByPage(-1); });
    nextBtn.addEventListener("click", function () { scrollByPage(1); });
    viewport.addEventListener("scroll", updateNav, { passive: true });
    window.addEventListener("resize", updateNav);
    updateNav();
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  Array.prototype.slice
    .call(document.querySelectorAll(".dc-slider"))
    .forEach(initSlider);

  Array.prototype.slice
    .call(document.querySelectorAll("[data-featured-strip]"))
    .forEach(initFeaturedStrip);
})();
